const normalizePreorderValue = (value) =>
  String(value || "")
    .replace(/\u00a0/g, " ")
    .trim()
    .toLowerCase();

const buildShopifySizedImageUrl = (url, width) => {
  if (!url) return "";

  const cleanUrl = url.startsWith("//") ? `https:${url}` : url;
  const queryIndex = cleanUrl.indexOf("?");
  const baseUrl = queryIndex === -1 ? cleanUrl : cleanUrl.slice(0, queryIndex);
  const query = queryIndex === -1 ? "" : cleanUrl.slice(queryIndex);
  const extensionIndex = baseUrl.lastIndexOf(".");

  if (extensionIndex === -1) return cleanUrl;

  return `${baseUrl.slice(0, extensionIndex)}_${width}x${baseUrl.slice(
    extensionIndex
  )}${query}`;
};

const buildPreorderSizeGuideUrl = (url) => {
  if (!url) return "#";

  try {
    const parsedUrl = new URL(url, window.location.origin);
    parsedUrl.searchParams.set("openSizeGuide", "true");
    return `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`;
  } catch (error) {
    const separator = url.includes("?") ? "&" : "?";
    return `${url}${separator}openSizeGuide=true`;
  }
};

const buildPreorderSectionRenderUrl = (url, sectionId = "main") => {
  if (!url) return "";

  try {
    const parsedUrl = new URL(url, window.location.origin);
    parsedUrl.searchParams.delete("openSizeGuide");
    parsedUrl.searchParams.set("section_id", sectionId);
    return `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`;
  } catch (error) {
    const separator = url.includes("?") ? "&" : "?";
    return `${url}${separator}section_id=${sectionId}`;
  }
};

const preorderSizeGuideCache = new Map();

const openPreorderSizeGuideModal = (modal) => {
  if (!modal) return;

  modal.classList.remove("hidden");
  document.body.style.overflow = "hidden";

  const scrollContainer = modal.querySelector(".size-guide__inner");
  if (scrollContainer) {
    scrollContainer.scrollTo({
      top: 0,
      behavior: "auto",
    });
  }
};

const closePreorderSizeGuideModal = (modal) => {
  if (!modal) return;

  modal.classList.add("hidden");
  document.body.style.overflow = "auto";
};

const initPreorderSizeGuideTabs = (root) => {
  const tabs = Array.from(root.querySelectorAll("[data-size-guide-tab]"));
  const panels = Array.from(root.querySelectorAll("[data-size-guide-panel]"));
  if (!tabs.length || !panels.length) return;

  const showPanel = (key) => {
    tabs.forEach((tab) =>
      tab.classList.toggle(
        "active",
        tab.getAttribute("data-size-guide-tab") === key
      )
    );
    panels.forEach((panel) =>
      panel.classList.toggle(
        "active",
        panel.getAttribute("data-size-guide-panel") === key
      )
    );

  };

  tabs.forEach((tab) => {
    tab.addEventListener("click", () => {
      showPanel(tab.getAttribute("data-size-guide-tab"));
    });
  });

  showPanel("chart");
};

const initPreorderSizeGuideChartSubtabs = (root) => {
  const wrap = root.querySelector(
    ".size-guide__subtabs:not(.size-guide__subtabs--measure)"
  );
  if (!wrap) return;

  const subtabs = Array.from(wrap.querySelectorAll("[data-size-guide-subtab]"));
  const tables = Array.from(root.querySelectorAll("[data-size-guide-table]"));
  if (!subtabs.length || !tables.length) return;

  const show = (key) => {
    subtabs.forEach((button) =>
      button.classList.toggle(
        "active",
        button.getAttribute("data-size-guide-subtab") === key
      )
    );
    tables.forEach((table) =>
      table.classList.toggle(
        "active",
        table.getAttribute("data-size-guide-table") === key
      )
    );
  };

  const toggle = () => {
    const active = wrap.querySelector("[data-size-guide-subtab].active");
    const nextKey =
      active?.getAttribute("data-size-guide-subtab") === "garment"
        ? "body"
        : "garment";
    show(nextKey);
  };

  subtabs.forEach((button) => {
    button.addEventListener("click", () => {
      show(button.getAttribute("data-size-guide-subtab"));
    });
  });

  wrap.addEventListener("click", (event) => {
    if (event.target.closest("[data-size-guide-subtab]")) return;
    toggle();
  });

  show("body");
};

const initPreorderSizeGuideMeasureSubtabs = (root) => {
  const wrap = root.querySelector(".size-guide__subtabs--measure");
  if (!wrap) return;

  const subtabs = Array.from(wrap.querySelectorAll("[data-size-guide-measure]"));
  const panels = Array.from(
    root.querySelectorAll("[data-size-guide-measure-panel]")
  );
  if (!subtabs.length || !panels.length) return;

  const show = (key) => {
    subtabs.forEach((button) =>
      button.classList.toggle(
        "active",
        button.getAttribute("data-size-guide-measure") === key
      )
    );
    panels.forEach((panel) =>
      panel.classList.toggle(
        "active",
        panel.getAttribute("data-size-guide-measure-panel") === key
      )
    );
  };

  const toggle = () => {
    const active = wrap.querySelector("[data-size-guide-measure].active");
    const nextKey =
      active?.getAttribute("data-size-guide-measure") === "garment"
        ? "body"
        : "garment";
    show(nextKey);
  };

  subtabs.forEach((button) => {
    button.addEventListener("click", () => {
      show(button.getAttribute("data-size-guide-measure"));
    });
  });

  wrap.addEventListener("click", (event) => {
    if (event.target.closest("[data-size-guide-measure]")) return;
    toggle();
  });

  show("body");
};

const initPreorderSizeGuideUnitSwatch = (root) => {
  const swatches = Array.from(root.querySelectorAll(".guide-swatch"));
  if (!swatches.length) return;

  swatches.forEach((swatch) => {
    const unitButtons = Array.from(
      swatch.querySelectorAll(".guide__swatch--type")
    );
    if (!unitButtons.length) return;

    const setUnit = (unit) => {
      const tableSpans = root.querySelectorAll(".guide-table td span");

      unitButtons.forEach((button) =>
        button.classList.toggle(
          "active",
          button.getAttribute("data-type") === unit
        )
      );

      const slider = swatch.querySelector(".guide__swatch--slider");
      if (slider) {
        slider.classList.toggle("active-in", unit === "in");
        slider.classList.toggle("active-cm", unit === "cm");
      }

      tableSpans.forEach((span) => span.classList.toggle("hidden"));
    };

    const toggleUnit = () => {
      const active = swatch.querySelector(".guide__swatch--type.active");
      const nextUnit = active?.getAttribute("data-type") === "cm" ? "in" : "cm";
      setUnit(nextUnit);
    };

    unitButtons.forEach((button) => {
      button.addEventListener("click", () => {
        if (button.classList.contains("active")) return;
        setUnit(button.getAttribute("data-type"));
      });
    });

    swatch.addEventListener("click", (event) => {
      if (event.target.closest(".guide__swatch--type")) return;
      toggleUnit();
    });
  });
};

const initPreorderSizeGuideMobileColumns = (root) => {
  const mobileSizeOptions = Array.from(
    root.querySelectorAll(".mobile-size-options > div")
  );
  if (!mobileSizeOptions.length) return;

  const showHideMobileSizeColumns = () => {
    if (window.innerWidth < 750) {
      mobileSizeOptions.forEach((option) => {
        if (!option.classList.contains("active")) return;

        const tableContainer = option.closest(".js-size-table-container");
        if (!tableContainer) return;

        const columnOrder = Number(option.getAttribute("data-order"));
        if (!Number.isInteger(columnOrder) || columnOrder < 2) return;

        tableContainer
          .querySelectorAll(".guide-table tr th:not(:first-child)")
          .forEach((cell) => cell.classList.add("hidden"));
        tableContainer
          .querySelectorAll(".guide-table tr td:not(:first-child)")
          .forEach((cell) => cell.classList.add("hidden"));

        tableContainer
          .querySelectorAll(`.guide-table tr th:nth-child(${columnOrder})`)
          .forEach((cell) => cell.classList.remove("hidden"));
        tableContainer
          .querySelectorAll(`.guide-table tr td:nth-child(${columnOrder})`)
          .forEach((cell) => cell.classList.remove("hidden"));
      });

      return;
    }

    root
      .querySelectorAll(".guide-table tr th:not(:first-child)")
      .forEach((cell) => cell.classList.remove("hidden"));
    root
      .querySelectorAll(".guide-table tr td:not(:first-child)")
      .forEach((cell) => cell.classList.remove("hidden"));
  };

  showHideMobileSizeColumns();
  window.addEventListener("resize", showHideMobileSizeColumns);

  mobileSizeOptions.forEach((option) => {
    option.addEventListener("click", function () {
      const tableContainer = option.closest(".js-size-table-container");
      if (!tableContainer) return;

      tableContainer
        .querySelectorAll(".mobile-size-options > div")
        .forEach((element) => element.classList.remove("active"));
      this.classList.add("active");
      showHideMobileSizeColumns();
    });
  });
};

const initPreorderSizeGuideHowToMeasureButton = (root) => {
  const howToMeasureButton = root.querySelector(".measuring-guide-btn");
  if (!howToMeasureButton) return;

  howToMeasureButton.addEventListener("click", (event) => {
    event.preventDefault();

    const measureTab = Array.from(root.querySelectorAll("[data-size-guide-tab]")).find(
      (tab) => tab.getAttribute("data-size-guide-tab") === "measure"
    );
    if (measureTab) measureTab.click();

    const measuringGuide = root.querySelector("#measuring-guide");
    if (measuringGuide) {
      measuringGuide.scrollIntoView({
        behavior: "smooth",
      });
    }
  });
};

const initPreorderSizeGuideNavigateAndOpen = (root) => {
  root.querySelectorAll(".navigate-and-open-size-guide-btn").forEach((button) => {
    if (button.dataset.preorderNavigateInit === "true") return;
    button.dataset.preorderNavigateInit = "true";

    button.addEventListener("click", function (event) {
      event.preventDefault();

      const targetUrl = this.getAttribute("href");
      if (!targetUrl) return;

      try {
        const parsedUrl = new URL(targetUrl, window.location.origin);
        parsedUrl.searchParams.set("openSizeGuide", "true");
        window.location.href = parsedUrl.toString();
      } catch (error) {
        const separator = targetUrl.includes("?") ? "&" : "?";
        window.location.href = `${targetUrl}${separator}openSizeGuide=true`;
      }
    });
  });
};

const initPreorderSizeGuideModal = (root) => {
  if (!root || root.dataset.preorderSizeGuideInitialized === "true") return root;

  root.dataset.preorderSizeGuideInitialized = "true";

  const closeButton = root.querySelector(".size-guide__close");
  if (closeButton) {
    closeButton.addEventListener("click", () => closePreorderSizeGuideModal(root));
  }

  root.addEventListener("click", (event) => {
    if (event.target === root) closePreorderSizeGuideModal(root);
  });

  window.addEventListener("keydown", (event) => {
    if (event.key === "Escape" && !root.classList.contains("hidden")) {
      closePreorderSizeGuideModal(root);
    }
  });

  initPreorderSizeGuideTabs(root);
  initPreorderSizeGuideChartSubtabs(root);
  initPreorderSizeGuideMeasureSubtabs(root);
  initPreorderSizeGuideUnitSwatch(root);
  initPreorderSizeGuideMobileColumns(root);
  initPreorderSizeGuideHowToMeasureButton(root);
  initPreorderSizeGuideNavigateAndOpen(root);

  return root;
};

const extractPreorderSizeGuideModal = (html) => {
  if (!html) return null;

  const doc = new DOMParser().parseFromString(html, "text/html");
  const modal = doc.querySelector(".size-guide");
  if (!modal) return null;

  return modal.cloneNode(true);
};

const initPreorderGenderSwitch = () => {
  const root = document.querySelector(".preorder-products");
  const switches = Array.from(
    root?.querySelectorAll(".preorder-products__switch--gender") || []
  );
  const slider = root?.querySelector(".preorder-products__switch--slider");

  if (!root || !switches.length || !slider) return;

  const mensProducts = root.querySelectorAll(".product-card--men");
  const womensProducts = root.querySelectorAll(".product-card--women");
  const urlParams = new URLSearchParams(window.location.search);

  const getInitialGender = () => {
    const requestedGender = urlParams.get("load")?.toLowerCase();
    return requestedGender === "women" ? "women" : "men";
  };

  const applyGender = (gender) => {
    const normalizedGender = gender === "women" ? "women" : "men";

    switches.forEach((button) => {
      button.classList.toggle(
        "active",
        button.getAttribute("data-gender") === normalizedGender
      );
    });

    slider.classList.remove("active-men", "active-women");
    slider.classList.add(
      normalizedGender === "women" ? "active-women" : "active-men"
    );

    mensProducts.forEach((product) => {
      product.classList.toggle("hidden", normalizedGender === "women");
    });

    womensProducts.forEach((product) => {
      product.classList.toggle("hidden", normalizedGender !== "women");
    });
  };

  switches.forEach((button) => {
    button.addEventListener("click", () => {
      applyGender(button.getAttribute("data-gender"));
    });
  });

  applyGender(getInitialGender());
};

const initPreorderConfirmation = () => {
  const popup = document.querySelector("[data-preorder-confirmation]");
  const confirmButton = popup?.querySelector(".preorder-confirmation__add-to-cart");
  const closeButton = popup?.querySelector(".preorder-confirmation__close");
  const text = popup?.querySelector("[data-preorder-confirmation-text]");

  if (!popup || !confirmButton || !closeButton || !text) {
    return {
      open: ({ button, variant }) => addPreorderVariantToCart(button, variant.id),
    };
  }

  let pendingAction = null;

  const close = () => {
    popup.classList.add("hidden");
    document.body.style.overflow = "auto";
    pendingAction = null;
  };

  const open = ({ button, variant }) => {
    if (!variant?.id) return;

    pendingAction = {
      button,
      variantId: variant.id,
    };

    text.textContent = variant.shipping
      ? `This is a pre-order item. ${variant.shipping}`
      : "This is a pre-order item.";

    popup.classList.remove("hidden");
    document.body.style.overflow = "hidden";
  };

  closeButton.addEventListener("click", close);
  popup.addEventListener("click", (event) => {
    if (event.target === popup) close();
  });

  window.addEventListener("keydown", (event) => {
    if (event.key === "Escape" && !popup.classList.contains("hidden")) {
      close();
    }
  });

  confirmButton.addEventListener("click", (event) => {
    event.preventDefault();
    if (!pendingAction) return;

    const { button, variantId } = pendingAction;
    close();
    addPreorderVariantToCart(button, variantId);
  });

  return { open, close };
};

const addPreorderVariantToCart = async (button, variantId) => {
  if (!variantId) return;

  const cartDrawer = document.querySelector("cart-drawer");
  const payload = {
    items: [
      {
        id: variantId,
        quantity: 1,
        properties: {
          "_Pre-order": "This is a pre-order item.",
        },
      },
    ],
  };

  if (cartDrawer) {
    payload.sections = cartDrawer.getSectionsToRender().map((section) => section.id);
    payload.sections_url = window.location.pathname;
    cartDrawer.setActiveElement(document.activeElement);
  }

  button.classList.add("loading");
  button.disabled = true;

  try {
    const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Requested-With": "XMLHttpRequest",
      },
      body: JSON.stringify(payload),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.description || "Failed to add pre-order item.");
    }

    if (cartDrawer) {
      cartDrawer.renderContents(data);
      cartDrawer.openCloseCart();
      cartDrawer.classList.remove("is-empty");
      return;
    }

    window.location.assign("/cart");
  } catch (error) {
    console.error(error);
  } finally {
    button.classList.remove("loading");
    button.disabled = false;
  }
};

const fetchPreorderSizeGuideModal = async (cacheKey, url) => {
  if (!url) return null;

  if (preorderSizeGuideCache.has(cacheKey)) {
    return preorderSizeGuideCache.get(cacheKey);
  }

  const modalPromise = (async () => {
    let lastError = null;
    const requestUrls = [
      buildPreorderSectionRenderUrl(url),
      url,
    ].filter(Boolean);

    for (const requestUrl of requestUrls) {
      try {
        const response = await fetch(requestUrl, {
          credentials: "same-origin",
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch size guide from ${requestUrl}.`);
        }

        const html = await response.text();
        const modal = extractPreorderSizeGuideModal(html);

        if (!modal) continue;

        modal.classList.add("hidden");
        modal.setAttribute("data-preorder-size-guide-modal", "true");
        document.body.appendChild(modal);

        return initPreorderSizeGuideModal(modal);
      } catch (error) {
        lastError = error;
      }
    }

    if (lastError) {
      console.error(lastError);
    }

    preorderSizeGuideCache.delete(cacheKey);
    return null;
  })();

  preorderSizeGuideCache.set(cacheKey, modalPromise);
  return modalPromise;
};

const initPreorderCard = (card, preorderConfirmation) => {
  if (!card || card.dataset.preorderInit === "true") return;

  let variants = [];

  try {
    variants = JSON.parse(
      card.dataset.variants || card.dataset.preorderVariants || "[]"
    );
  } catch (error) {
    console.error("Invalid pre-order variants payload", error);
    return;
  }

  if (!variants.length) return;

  card.dataset.preorderInit = "true";

  const productLinks = Array.from(card.querySelectorAll(".product-card__link"));
  const getImage = () => card.querySelector("[data-preorder-image]");
  let swatches = Array.from(card.querySelectorAll(".color-swatch[data-color]"));
  const sizeButtons = Array.from(card.querySelectorAll(".product-card__size-btn"));
  const colorValue = card.querySelector(".product-card__option-value");
  const sizeValue = card.querySelector(".product-card__size-value");
  const priceValue = card.querySelector("[data-preorder-price]");
  const shippingValue = card.querySelector("[data-preorder-shipping]");
  const atcButton = card.querySelector(".product-card__preorder-atc");
  const detailsLink = card.querySelector("[data-preorder-details-link]");
  const sizeGuideTrigger = card.querySelector("[data-preorder-size-guide-trigger]");
  const productHandle = card.dataset.productHandle;
  const imageContainer = card.querySelector(".product-card__image .image-container");
  let productDataPromise;
  let mediaRenderToken = 0;

  const getProductData = async () => {
    if (!productHandle) return null;

    if (!productDataPromise) {
      productDataPromise = fetch(`${window.Shopify.routes.root}products/${productHandle}.js`)
        .then((response) => {
          if (!response.ok) {
            throw new Error(`Failed to fetch product data for ${productHandle}.`);
          }

          return response.json();
        })
        .catch((error) => {
          console.error(error);
          return null;
        });
    }

    return productDataPromise;
  };

  const buildPreorderImageMarkup = (src, alt, extraClass = "") => {
    const classes = ["image-cover", "product-card__preorder-image", extraClass]
      .filter(Boolean)
      .join(" ");

    return `
      <img
        src="${buildShopifySizedImageUrl(src, 900)}"
        alt="${alt || ""}"
        class="${classes}"
        loading="lazy"
        sizes="(min-width: 990px) calc((100vw - 120px) / 3), (min-width: 750px) calc((100vw - 80px) / 2), calc(100vw - 48px)"
        srcset="
          ${buildShopifySizedImageUrl(src, 320)} 320w,
          ${buildShopifySizedImageUrl(src, 480)} 480w,
          ${buildShopifySizedImageUrl(src, 640)} 640w,
          ${buildShopifySizedImageUrl(src, 800)} 800w,
          ${buildShopifySizedImageUrl(src, 900)} 900w
        "
        data-preorder-image="true"
      >
    `;
  };

  const setSizeGuideTriggerLoading = (isLoading) => {
    if (!sizeGuideTrigger) return;

    sizeGuideTrigger.disabled = isLoading;
    sizeGuideTrigger.setAttribute("aria-busy", isLoading ? "true" : "false");
  };

  const openSizeGuideForCurrentProduct = async () => {
    if (!sizeGuideTrigger) return;

    const url =
      sizeGuideTrigger.dataset.preorderSizeGuideUrl ||
      detailsLink?.getAttribute("href") ||
      "";

    if (!url) return;

    setSizeGuideTriggerLoading(true);

    try {
      const cacheKey = productHandle || url;
      const modal = await fetchPreorderSizeGuideModal(cacheKey, url);

      if (modal) {
        openPreorderSizeGuideModal(modal);
        return;
      }

      window.location.assign(buildPreorderSizeGuideUrl(url));
    } finally {
      setSizeGuideTriggerLoading(false);
    }
  };

  const updatePreorderCardImageForColor = async (color) => {
    if (!imageContainer || !productHandle) return;

    mediaRenderToken += 1;
    const currentMediaRenderToken = mediaRenderToken;
    const productData = await getProductData();

    if (!productData || currentMediaRenderToken !== mediaRenderToken) return;

    const normalizedColor = normalizePreorderValue(color);
    let matchedVariantIndex = -1;

    for (let index = 0; index < (productData.variants || []).length; index += 1) {
      const productVariant = productData.variants[index];

      if (
        normalizePreorderValue(productVariant.option1) === normalizedColor &&
        productVariant.featured_image?.src
      ) {
        matchedVariantIndex = index;
        break;
      }
    }

    const matchedVariant =
      matchedVariantIndex === -1 ? null : productData.variants[matchedVariantIndex];
    const colorMedia = Array.isArray(productData.media)
      ? productData.media.filter(
          (media) =>
            media.media_type === "image" &&
            normalizePreorderValue(media.alt) === normalizedColor
        )
      : [];
    const featuredSrc = matchedVariant?.featured_image?.src || colorMedia[0]?.src;

    if (!featuredSrc) return;

    const featuredSrcBase = featuredSrc.split("?")[0];
    const imageAlt = color || matchedVariant?.option1 || "";
    let imagesMarkup = buildPreorderImageMarkup(featuredSrc, imageAlt);

    if (colorMedia.length > 1) {
      const hoverImage = colorMedia[colorMedia.length - 1];
      const hoverSrc = hoverImage?.src || "";
      const hoverSrcBase = hoverSrc.split("?")[0];

      if (hoverSrcBase && hoverSrcBase !== featuredSrcBase) {
        imagesMarkup += buildPreorderImageMarkup(
          hoverSrc,
          imageAlt,
          "product-card__image--hover"
        );
      }
    }

    imageContainer.innerHTML = imagesMarkup.trim();
  };

  if (atcButton && !atcButton.dataset.defaultLabel) {
    atcButton.dataset.defaultLabel = atcButton.textContent.trim() || "Pre-Order";
  }

  const isPurchasablePreorderVariant = (variant) =>
    Boolean(variant?.isPreorder && variant?.available);

  const getVariant = (color, size) =>
    variants.find(
      (variant) =>
        normalizePreorderValue(variant.color) === normalizePreorderValue(color) &&
        normalizePreorderValue(variant.size) === normalizePreorderValue(size)
    );

  const getColorVariants = (color) =>
    variants.filter(
      (variant) =>
        normalizePreorderValue(variant.color) === normalizePreorderValue(color)
    );

  const hasPurchasablePreorderColor = (color) =>
    getColorVariants(color).some((variant) => isPurchasablePreorderVariant(variant));

  swatches = swatches.filter((swatch) => {
    const keep = hasPurchasablePreorderColor(swatch.dataset.color);
    if (!keep) swatch.remove();
    return keep;
  });

  const getFirstPreorderVariant = (color) =>
    getColorVariants(color).find((variant) => variant.isPreorder);

  const getFirstAvailableVariant = (color) =>
    getColorVariants(color).find((variant) => isPurchasablePreorderVariant(variant));

  const getFallbackVariant = (color) =>
    getFirstAvailableVariant(color) ||
    getFirstPreorderVariant(color) ||
    getColorVariants(color).find((variant) => variant.available) ||
    getColorVariants(color)[0] ||
    variants[0];

  const _pickRandomInitialVariant = () => {
    const sessionKey = "preorder_swatch_seed";
    let seed = sessionStorage.getItem(sessionKey);
    if (!seed) {
      seed = String(Math.floor(Math.random() * 1e15));
      sessionStorage.setItem(sessionKey, seed);
    }
    const productId = card.dataset.productId || "";
    const combined = seed + "_" + productId;
    let hash = 0;
    for (let i = 0; i < combined.length; i++) {
      hash = (hash * 31 + combined.charCodeAt(i)) >>> 0;
    }
    const availableColors = [...new Set(
      variants
        .filter((v) => isPurchasablePreorderVariant(v))
        .map((v) => v.color)
    )];
    if (!availableColors.length) return null;
    const color = availableColors[hash % availableColors.length];
    return getFirstAvailableVariant(color) || null;
  };

  const initialVariant =
    _pickRandomInitialVariant() ||
    variants.find((variant) => isPurchasablePreorderVariant(variant)) ||
    variants.find((variant) => variant.isPreorder) ||
    variants[0];
  let selectedColor = initialVariant?.color || "";
  let selectedSize = initialVariant?.size || "";

  const syncSwatches = () => {
    swatches.forEach((swatch) => {
      const isActive =
        normalizePreorderValue(swatch.dataset.color) ===
        normalizePreorderValue(selectedColor);
      swatch.classList.toggle("active", isActive);
    });
  };

  const syncSizeButtons = () => {
    sizeButtons.forEach((button) => {
      const variant = getVariant(selectedColor, button.dataset.size);
      const isAvailable = isPurchasablePreorderVariant(variant);
      const isActive =
        normalizePreorderValue(button.dataset.size) ===
        normalizePreorderValue(selectedSize);

      button.disabled = !isAvailable;
      button.classList.toggle("disabled", !isAvailable);
      button.classList.toggle("active", isAvailable && isActive);

      if (variant?.id) {
        button.dataset.variantId = variant.id;
      } else {
        delete button.dataset.variantId;
      }
    });
  };

  const render = (variant) => {
    if (!variant) return;

    selectedColor = variant.color;
    selectedSize = variant.size;
    card.dataset.preorderCurrentColor = variant.color || "";
    card.dataset.preorderCurrentSize = variant.size || "";

    if (colorValue) colorValue.textContent = variant.color || "";
    if (sizeValue) sizeValue.textContent = variant.size || "";
    if (priceValue) priceValue.textContent = variant.priceText || "";
    if (shippingValue) shippingValue.textContent = variant.shipping || "";

    if (atcButton) {
      atcButton.dataset.variantId = variant.id;
      atcButton.disabled = !isPurchasablePreorderVariant(variant);
      atcButton.textContent = !variant.available
        ? "Sold Out"
        : variant.isPreorder
        ? atcButton.dataset.defaultLabel || "Pre-Order"
        : "Unavailable";
    }

    const image = getImage();
    if (image && variant.imageSrc) {
      image.src = variant.imageSrc;
      image.srcset = `${variant.imageSrc} 900w`;
      image.alt = variant.imageAlt || variant.color || "";
    }

    productLinks.forEach((link) => {
      if (variant.url) link.setAttribute("href", variant.url);
    });

    if (detailsLink && variant.url) {
      detailsLink.setAttribute("href", variant.url);
    }

    if (sizeGuideTrigger && variant.url) {
      sizeGuideTrigger.dataset.preorderSizeGuideUrl = variant.url;
    }

    syncSwatches();
    syncSizeButtons();
  };

  swatches.forEach((swatch) => {
    swatch.addEventListener("click", (event) => {
      event.preventDefault();
      event.stopPropagation();

      const color = swatch.dataset.color;
      const preferredVariant = getVariant(color, selectedSize);
      const nextVariant =
        (preferredVariant &&
          isPurchasablePreorderVariant(preferredVariant) &&
          preferredVariant) ||
        getFirstAvailableVariant(color) ||
        preferredVariant ||
        getFallbackVariant(color);

      render(nextVariant);
      updatePreorderCardImageForColor(nextVariant.color || color);
    });
  });

  sizeButtons.forEach((button) => {
    button.addEventListener("click", (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (button.disabled) return;

      const nextVariant = getVariant(selectedColor, button.dataset.size);
      if (!isPurchasablePreorderVariant(nextVariant)) return;

      render(nextVariant);
    });
  });

  atcButton?.addEventListener("click", (event) => {
    event.preventDefault();

    const variant =
      getVariant(selectedColor, selectedSize) ||
      getFirstAvailableVariant(selectedColor) ||
      getFallbackVariant(selectedColor);
    if (!variant?.id || atcButton.disabled) return;

    preorderConfirmation.open({
      button: atcButton,
      variant,
    });
  });

  sizeGuideTrigger?.addEventListener("click", (event) => {
    event.preventDefault();
    event.stopPropagation();
    openSizeGuideForCurrentProduct();
  });

  render(initialVariant);
  updatePreorderCardImageForColor(initialVariant.color || selectedColor);
};

const initPreorderCards = () => {
  const preorderConfirmation = initPreorderConfirmation();

  document
    .querySelectorAll(".product-card.preorder-collection")
    .forEach((card) => initPreorderCard(card, preorderConfirmation));
};

document.addEventListener("DOMContentLoaded", () => {
  initPreorderGenderSwitch();
  initPreorderCards();
  document.querySelector(".preorder-products")?.classList.remove("blank");
});
